import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

/* ─── Winning combos ─────────────────────────────── */
const WINNING_LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function checkWinner(board) {
  for (const [a, b, c] of WINNING_LINES) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line: [a, b, c] };
    }
  }
  if (board.every(Boolean)) return { winner: "draw", line: [] };
  return null;
}

function aiMove(board) {
  const empty = board.map((v, i) => (!v ? i : null)).filter((i) => i !== null);
  if (!empty.length) return null;
  if (Math.random() < 0.6) return empty[Math.floor(Math.random() * empty.length)];
  for (const i of empty) {
    const b = [...board]; b[i] = "LOGO";
    if (checkWinner(b)?.winner === "LOGO") return i;
  }
  for (const i of empty) {
    const b = [...board]; b[i] = "O";
    if (checkWinner(b)?.winner === "O") return i;
  }
  if (empty.includes(4)) return 4;
  const corners = [0, 2, 6, 8].filter((c) => empty.includes(c));
  if (corners.length) return corners[Math.floor(Math.random() * corners.length)];
  return empty[Math.floor(Math.random() * empty.length)];
}

/* ─── Glowing Dot Canvas Background ─────────────── */
function GlowingDots() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const rafRef = useRef(null);
  const dotsRef = useRef([]);

  /* Build dot grid */
  const buildDots = useCallback((w, h) => {
    const gap = 36;
    const cols = Math.ceil(w / gap) + 1;
    const rows = Math.ceil(h / gap) + 1;
    const dots = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        dots.push({ x: c * gap, y: r * gap, baseR: 1.5 });
      }
    }
    dotsRef.current = dots;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      buildDots(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMouseMove);

    /* Animation loop */
    const GLOW_RADIUS = 140;   // px — how far glow reaches
    const GLOW_COLOR  = [99, 102, 241]; // indigo #6366f1

    const draw = () => {
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      for (const dot of dotsRef.current) {
        const dx = dot.x - mx;
        const dy = dot.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const proximity = Math.max(0, 1 - dist / GLOW_RADIUS);

        const r = dot.baseR + proximity * 3.5;        // grow near cursor
        const alpha = 0.12 + proximity * 0.88;        // fade in near cursor

        const [gr, gg, gb] = GLOW_COLOR;
        // Blend between dim grey and vivid indigo based on proximity
        const red   = Math.round(210  + (gr - 210)  * proximity);
        const green = Math.round(215  + (gg - 215)  * proximity);
        const blue  = Math.round(220  + (gb - 220)  * proximity);

        ctx.beginPath();
        ctx.arc(dot.x, dot.y, r, 0, Math.PI * 2);

        // Add a soft outer glow for closest dots
        if (proximity > 0.3) {
          const grd = ctx.createRadialGradient(dot.x, dot.y, 0, dot.x, dot.y, r * 3.5);
          grd.addColorStop(0, `rgba(${gr}, ${gg}, ${gb}, ${proximity * 0.35})`);
          grd.addColorStop(1, `rgba(${gr}, ${gg}, ${gb}, 0)`);
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, r * 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, r, 0, Math.PI * 2);
        }

        ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [buildDots]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
}

/* ─── Logo cell ──────────────────────────────────── */
function LogoCell({ animate }) {
  return (
    <div className={`ttt-logo-cell ${animate ? "ttt-pop" : ""}`}>
      <img src="/logo.png" alt="Sinix Studio" className="ttt-logo-img" />
    </div>
  );
}

/* ─── O cell ─────────────────────────────────────── */
function OCell({ animate, isWinning }) {
  return (
    <div className={`ttt-o-cell ${animate ? "ttt-pop" : ""} ${isWinning ? "ttt-winning" : ""}`}>
      <svg viewBox="0 0 60 60" fill="none" className="ttt-o-svg">
        <circle
          cx="30" cy="30" r="22"
          stroke="url(#oGrad)" strokeWidth="6"
          strokeLinecap="round"
          className="ttt-o-path"
        />
        <defs>
          <linearGradient id="oGrad" x1="0" y1="0" x2="60" y2="60" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

/* ─── Main Game ──────────────────────────────────── */
export default function TicTacToe() {
  const [board, setBoard]       = useState(Array(9).fill(null));
  const [isUserTurn, setIsUserTurn] = useState(true);
  const [result, setResult]     = useState(null);
  const [animCells, setAnimCells] = useState([]);
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    if (!isUserTurn && !result) {
      setThinking(true);
      const timer = setTimeout(() => {
        setBoard((prev) => {
          const move = aiMove(prev);
          if (move === null) return prev;
          const next = [...prev];
          next[move] = "LOGO";
          const won = checkWinner(next);
          if (won) setResult(won);
          setAnimCells((a) => [...a, move]);
          return next;
        });
        setIsUserTurn(true);
        setThinking(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isUserTurn, result]);

  const handleClick = (idx) => {
    if (!isUserTurn || board[idx] || result || thinking) return;
    const next = [...board];
    next[idx] = "O";
    const won = checkWinner(next);
    setAnimCells((a) => [...a, idx]);
    setBoard(next);
    if (won) { setResult(won); return; }
    setIsUserTurn(false);
  };

  const reset = () => {
    setBoard(Array(9).fill(null));
    setIsUserTurn(true);
    setResult(null);
    setAnimCells([]);
    setThinking(false);
  };

  const winLine = result?.line ?? [];

  return (
    <div className="ttt-page">
      {/* ── Interactive glowing dots ── */}
      <GlowingDots />

      {/* ── Radial vignette + gradient overlay ── */}
      <div className="ttt-overlay" aria-hidden="true" />

      <div className="ttt-wrapper">
        {/* Header */}
        <div className="ttt-header">
          <div className="ttt-badge">// mini game</div>
          <h1 className="ttt-title">
            Tic <span className="ttt-accent">Tac</span> Toe
          </h1>
          <p className="ttt-subtitle">
            You are <span className="ttt-you">○</span> · Sinix Studio is{" "}
            <img src="/logo.png" alt="logo" className="ttt-inline-logo" />
          </p>
        </div>

        {/* Status bar */}
        <div className={`ttt-status ${result ? "ttt-status-done" : ""}`}>
          {result ? (
            result.winner === "O" ? (
              <span className="ttt-status-win">🎉 You Win!</span>
            ) : result.winner === "draw" ? (
              <span className="ttt-status-draw">🤝 It's a Draw!</span>
            ) : (
              <span className="ttt-status-lose">😅 Sinix Studio Wins!</span>
            )
          ) : thinking ? (
            <span className="ttt-status-thinking">
              <span className="ttt-dot-1">·</span>
              <span className="ttt-dot-2">·</span>
              <span className="ttt-dot-3">·</span>
              &nbsp;Sinix is thinking
            </span>
          ) : isUserTurn ? (
            <span>Your turn — tap a cell</span>
          ) : (
            <span>Sinix Studio is thinking…</span>
          )}
        </div>

        {/* Board */}
        <div className="ttt-board">
          {board.map((cell, idx) => {
            const isWinning = winLine.includes(idx);
            const anim = animCells.includes(idx);
            return (
              <button
                key={idx}
                className={`ttt-cell ${!cell && !result && isUserTurn && !thinking ? "ttt-cell-hover" : ""} ${isWinning ? "ttt-cell-win" : ""} ${!cell ? "ttt-cell-empty" : ""}`}
                onClick={() => handleClick(idx)}
                aria-label={`Cell ${idx + 1} ${cell || "empty"}`}
              >
                {cell === "LOGO" && <LogoCell animate={anim} />}
                {cell === "O" && <OCell animate={anim} isWinning={isWinning} />}
              </button>
            );
          })}
        </div>

        {/* Game over */}
        {result && (
          <div className="ttt-gameover">
            <div className="ttt-message-card">
              {result.winner === "O" ? (
                <>
                  <div className="ttt-msg-emoji">🏆</div>
                  <h2 className="ttt-msg-title">Wow you are smart!</h2>
                  <p className="ttt-msg-body">
                    You definitely should choose{" "}
                    <strong>Sinix Studio</strong> — only smart people do.
                  </p>
                </>
              ) : result.winner === "draw" ? (
                <>
                  <div className="ttt-msg-emoji">🤝</div>
                  <h2 className="ttt-msg-title">Evenly matched!</h2>
                  <p className="ttt-msg-body">
                    A tie means you're already thinking like us. How about
                    working together?
                  </p>
                </>
              ) : (
                <>
                  <div className="ttt-msg-emoji">💭</div>
                  <h2 className="ttt-msg-title">Maybe you need help…</h2>
                  <p className="ttt-msg-body">
                    Can you choose <strong>Sinix Studio</strong> as your design partner? We
                    promise to go easy on you. 😄
                  </p>
                </>
              )}

              <div className="ttt-actions">
                <button className="ttt-btn-retry" onClick={reset}>
                  🔄 Retry
                </button>
                <Link to="/" className="ttt-btn-home">
                  🏠 Back to Home
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
